/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { createPath, matchPath, type Path, parsePath, type To } from 'react-router-dom';
import type { DesktopApp } from '../types';

export const toPath = (to: To) => (typeof to === 'string' ? to : createPath(to as Partial<Path>));

// PVSim pages as linked from main (emails, Excel exports, agent resources, saved bookmarks).
const legacyPvsimPaths: Readonly<Record<string, string>> = {
  '/pv/pvsim': '/pv/pvsim/results',
  '/pv/pvsim/compare': '/pv/pvsim/results/compare',
  '/pv/pvsim/configurations': '/pv/pvsim/config',
  '/pv/pvsim/configurations/compare': '/pv/pvsim/config/compare',
  '/pv/pvsim/simulate': '/pv/pvsim/new',
};

const normalizeLegacyPvsimPath = (path: string) => {
  const { pathname = '', search = '', hash = '' } = parsePath(path);
  // On main `/pv/pvsim/results?scenario=` was the result detail page, now `/pv/pvsim/result`.
  const isLegacyDetail =
    pathname === '/pv/pvsim/results' && new URLSearchParams(search).has('scenario');
  const current = isLegacyDetail ? '/pv/pvsim/result' : legacyPvsimPaths[pathname];
  return current ? `${current}${search}${hash}` : path;
};

/** Rewrites retired path aliases (`/next/*`, `/components/*`, main's PVSim paths). */
export const normalizeAppPath = (path: string) =>
  normalizeLegacyPvsimPath(
    path.replace(/^\/next(?=[/?#]|$)/, '').replace(/^\/components(?=[/?#]|$)/, '/db') || '/',
  );

/**
 * How specifically `app` claims `path`: the segment count of its longest matching route
 * pattern, or -1 when none match.
 */
export const getAppPathSpecificity = (app: DesktopApp, path: string) => {
  const pathname = parsePath(path).pathname || '/';
  return app.routes.reduce(
    (best, pattern) =>
      matchPath({ path: pattern, end: false }, pathname)
        ? Math.max(best, pattern.split('/').filter(Boolean).length)
        : best,
    -1,
  );
};

/** Returns the app whose route pattern matches `path` most specifically. */
export const resolveAppForPath = (
  apps: readonly DesktopApp[],
  path: string,
): DesktopApp | undefined => {
  let bestApp: DesktopApp | undefined;
  let bestSpecificity = -1;
  for (const app of apps) {
    const specificity = getAppPathSpecificity(app, path);
    if (specificity > bestSpecificity) {
      bestApp = app;
      bestSpecificity = specificity;
    }
  }

  return bestApp;
};
